import { useState, useEffect } from 'react';
import { dgiiApi } from '../../api/dgii';
import { ClockIcon, DocumentTextIcon, TrashIcon, PlayIcon, ChevronLeftIcon, ChevronRightIcon, EyeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import ColaProcesamiento from './ColaProcesamiento';

export default function PendingInvoicesSection({ tipoCarga, onProcessComplete, onRefresh, empresaId, timestampInicioSesion }) {
	const [archivosPendientes, setArchivosPendientes] = useState([]);
	const [loading, setLoading] = useState(false);
	const [processing, setProcessing] = useState(false);
	const [resetting, setResetting] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const itemsPerPage = 5; // Mostrar 5 por defecto
	const [showImageModal, setShowImageModal] = useState(false);
	const [imageUrl, setImageUrl] = useState(null);

	useEffect(() => {
		loadPendingFiles();
	}, [onRefresh, tipoCarga]); // Recargar cuando cambia el trigger o el tipo de carga

	// También recargar cuando el componente se monta
	useEffect(() => {
		loadPendingFiles();
	}, []); // Solo al montar

	const loadPendingFiles = async () => {
		setLoading(true);
		try {
			const facturas = await dgiiApi.getFacturas();
			
			// Si no es un array, intentar convertir
			let facturasArray = [];
			if (Array.isArray(facturas)) {
				facturasArray = facturas;
			} else if (facturas && typeof facturas === 'object') {
				facturasArray = facturas.results || facturas.data || [];
			}
			
			// Filtrar solo las que están pendientes, sin formulario y del tipo de carga correcto
			const pendientes = facturasArray.filter(f => {
				if (!f || typeof f !== 'object') {
					return false;
				}
				
				const esPendiente = f.estado_procesamiento === 'pendiente';
				
				// Verificar tipo de carga
				// Si tipo_carga no existe o es null/undefined, asumir 'individual' (default del modelo)
				const tipoCargaFactura = f.tipo_carga || 'individual';
				const tipoCorrecto = tipoCargaFactura === tipoCarga;
				
				// Verificar si formulario es null, undefined, o un objeto vacío
				let sinFormulario = false;
				if (!f.formulario || f.formulario === null || f.formulario === undefined) {
					sinFormulario = true;
				} else if (typeof f.formulario === 'number') {
					sinFormulario = false;
				} else if (typeof f.formulario === 'object') {
					sinFormulario = !f.formulario.id && Object.keys(f.formulario).length === 0;
				} else {
					sinFormulario = false;
				}
				
				return esPendiente && sinFormulario && tipoCorrecto;
			});
			
			// Ordenar por fecha de subida descendente (más recientes primero)
			const ordenados = pendientes.sort((a, b) => {
				const fechaA = new Date(a.fecha_subida || 0);
				const fechaB = new Date(b.fecha_subida || 0);
				return fechaB - fechaA;
			});
			setArchivosPendientes(ordenados);
			setCurrentPage(1); // Resetear a la primera página cuando se cargan nuevos datos
		} catch (err) {
			setArchivosPendientes([]);
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = async (id) => {
		if (!window.confirm('¿Estás seguro de que deseas eliminar este archivo?')) {
			return;
		}

		try {
			await dgiiApi.deleteFactura(id);
			loadPendingFiles();
		} catch (err) {
			alert('Error al eliminar el archivo');
		}
	};

	const handleProcess = async () => {
		if (archivosPendientes.length === 0) {
			alert('No hay archivos pendientes para procesar');
			return;
		}

		setProcessing(true);
		try {
			// Procesar solo los archivos de este tipo de carga, con empresa seleccionada
			const result = await dgiiApi.procesarPendientes('606', tipoCarga, empresaId);
			
			// Verificar si se procesó con colas (asíncrono)
			if (result.modo === 'asíncrono_individual' || result.modo === 'asíncrono_lote') {
				// Procesamiento asíncrono - mostrar mensaje y componente de cola
				const mensaje = result.modo === 'asíncrono_lote'
					? `Se encolaron ${result.archivos_en_cola} archivo(s) para procesamiento asíncrono.`
					: `Se encolaron ${result.archivos_en_cola} archivo(s) para procesamiento asíncrono.`;
				
				alert(mensaje + '\n\nPuedes monitorear el progreso en el panel de estado de cola.');
				
				// Recargar después de un breve delay para que el backend procese
				setTimeout(() => {
					loadPendingFiles();
				}, 2000);
		} else {
			// Procesamiento síncrono
			// Reconsolidar formularios actualizados (garantiza que datos_extraidos esté correcto)
			if (result.formularios_ids && result.formularios_ids.length > 0) {
				for (const formId of result.formularios_ids) {
					try {
						await dgiiApi.reconsolidarFormulario(formId);
					} catch (reconErr) {
					}
				}
			}
			await loadPendingFiles();
			if (onProcessComplete) {
				onProcessComplete(result.formularios_ids || []);
			}
		}
		} catch (err) {
			alert('Error al procesar archivos: ' + (err.response?.data?.detail || err.message));
		} finally {
			setProcessing(false);
		}
	};

	const handleResetCola = async () => {
		if (!window.confirm(
			'¿Resetear la cola de procesamiento?\n\n' +
			'Esto hará lo siguiente:\n' +
			'• Purgar tareas pendientes en la cola de Celery\n' +
			'• Restablecer archivos en estado "error" o "procesando" a "pendiente"\n\n' +
			'Los archivos reseteados podrán volver a procesarse.'
		)) return;

		setResetting(true);
		try {
			const result = await dgiiApi.resetCola(null, { empresaId: empresaId });
			alert(
				`✅ Cola reseteada:\n` +
				`• ${result.cola_purgada ?? 0} tarea(s) eliminadas de Celery\n` +
				`• ${result.archivos_reseteados ?? 0} archivo(s) restablecidos a "pendiente"` +
				(result.error_purga ? `\n\n⚠️ Nota: ${result.error_purga}` : '')
			);
			await loadPendingFiles();
		} catch (err) {
			console.error('Error reseteando cola:', err);
			alert('Error al resetear la cola: ' + (err.response?.data?.detail || err.message));
		} finally {
			setResetting(false);
		}
	};

	const handleRescatarTodo = async () => {
		// Diagnóstico primero
		let diagMsg = '';
		try {
			const diag = await dgiiApi.diagnosticoEmpresa(empresaId);
			const aa = diag.archivos_adjuntos;
			diagMsg = `📊 Estado actual de archivos:\n` +
				`• Total: ${aa.total}\n` +
				`• Sin formulario: ${aa.sin_formulario}\n` +
				`• Por estado: ${JSON.stringify(aa.por_estado)}\n` +
				`• FacturaExtraida: ${diag.facturas_extraidas.total}\n\n` +
				`${diag.diagnostico}\n\n¿Deseas rescatar TODOS los archivos (incluyendo completados sin formulario)?`;
		} catch {
			diagMsg = '¿Rescatar todos los archivos? Esto reiniciará archivos en cualquier estado sin formulario asignado.';
		}

		if (!window.confirm(diagMsg)) return;

		setResetting(true);
		try {
			const result = await dgiiApi.resetCola(null, { rescateTodo: true, empresaId: empresaId });
			alert(
				`✅ Rescate completado:\n` +
				`• ${result.archivos_reseteados ?? 0} archivos en error/procesando → pendiente\n` +
				`• ${result.huerfanos_rescatados ?? 0} archivos completados sin formulario → pendiente\n\n` +
				`Ahora presiona "Procesar" para re-procesar todos.`
			);
			await loadPendingFiles();
		} catch (err) {
			alert('Error: ' + (err.response?.data?.detail || err.message));
		} finally {
			setResetting(false);
		}
	};

	const formatDate = (dateString) => {
		if (!dateString) return '-';
		const date = new Date(dateString);
		return date.toLocaleDateString('es-DO', {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit'
		});
	};

	const formatFileSize = (bytes) => {
		if (!bytes) return '-';
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
	};

	const getImageUrl = (archivo) => {
		// El serializer devuelve archivo_url como campo calculado
		if (archivo.archivo_url) {
			return archivo.archivo_url;
		}
		// Fallback: si viene como objeto archivo con url
		if (archivo.archivo && typeof archivo.archivo === 'string') {
			return archivo.archivo;
		}
		if (archivo.archivo?.url) {
			return archivo.archivo.url;
		}
		return null;
	};

	const viewImage = (url) => {
		if (url) {
			setImageUrl(url);
			setShowImageModal(true);
		}
	};

	if (loading) {
		return (
			<div className="bg-white rounded-lg shadow-md p-4">
				<div className="flex items-center justify-center py-4">
					<ClockIcon className="h-6 w-6 animate-spin text-primary" />
					<span className="ml-2 text-gray-600 text-sm">Cargando archivos pendientes...</span>
				</div>
			</div>
		);
	}

	if (archivosPendientes.length === 0) {
		return null; // No mostrar nada si no hay pendientes
	}

	// Calcular paginación
	const totalPages = Math.ceil(archivosPendientes.length / itemsPerPage);
	const startIndex = (currentPage - 1) * itemsPerPage;
	const endIndex = startIndex + itemsPerPage;
	const archivosPaginados = archivosPendientes.slice(startIndex, endIndex);

	return (
		<div className="space-y-4">
			{/* Componente de estado de cola - key forzará remount cuando cambie timestamp */}
			{timestampInicioSesion && (
				<ColaProcesamiento key={timestampInicioSesion} timestampInicio={timestampInicioSesion} />
			)}
			
			<div className="bg-white rounded-lg shadow-md p-4">
				{/* Título y badge en una sola línea */}
				<div className="flex items-center justify-between mb-2">
					<div className="flex items-center space-x-2 flex-1 min-w-0">
						<ClockIcon className="h-5 w-5 text-yellow-600 flex-shrink-0" />
						<h4 className="text-base font-bold text-gray-800 whitespace-nowrap">
							Facturas Pendientes
						</h4>
						<span className="text-sm text-gray-500 whitespace-nowrap">
							({tipoCarga === 'individual' ? 'Individual' : 'Masiva'})
						</span>
						<span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded-full flex-shrink-0">
							{archivosPendientes.length}
						</span>
					</div>
				</div>
			
			{/* Botones y paginación en una línea debajo del encabezado, alineados a la derecha */}
			<div className="flex items-center justify-end space-x-2 mb-3">
				{/* Botones de acción */}
				<button
					onClick={loadPendingFiles}
					disabled={loading}
					className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
					title="Actualizar lista"
				>
					<ClockIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
				</button>
				<button
					onClick={handleResetCola}
					disabled={resetting || processing}
					className="inline-flex items-center px-3 py-1.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-lg hover:bg-orange-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
					title="Resetear cola: purgar Celery y restablecer archivos con error/atascados a 'pendiente'"
				>
					{resetting ? (
						<ClockIcon className="h-4 w-4 animate-spin mr-1" />
					) : (
						<svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
						</svg>
					)}
					{resetting ? 'Reseteando...' : 'Reset Cola'}
				</button>
				<button
					onClick={handleRescatarTodo}
					disabled={resetting || processing}
					className="inline-flex items-center px-3 py-1.5 bg-red-100 text-red-700 text-xs font-medium rounded-lg hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
					title="Rescatar archivos perdidos: reinicia TODOS los archivos sin formulario (incluyendo los que quedaron como 'completado' por error)"
				>
					{resetting ? <ClockIcon className="h-4 w-4 animate-spin mr-1" /> : '🚑 '}
					{resetting ? 'Rescatando...' : 'Rescatar'}
				</button>
				<button
					onClick={handleProcess}
					disabled={processing || archivosPendientes.length === 0}
					className="inline-flex items-center px-4 py-1.5 bg-primary text-white text-xs font-medium rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
				>
					{processing ? (
						<>
							<ClockIcon className="h-4 w-4 animate-spin mr-1.5" />
							Procesando...
						</>
					) : (
						<>
							<PlayIcon className="h-4 w-4 mr-1.5" />
							Procesar
						</>
					)}
				</button>
				
				{/* Controles de paginación */}
				{archivosPendientes.length > itemsPerPage && (
					<div className="flex items-center space-x-2 ml-2 pl-2 border-l border-gray-300">
						<button
							onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
							disabled={currentPage === 1}
							className="inline-flex items-center px-2 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
							title="Página anterior"
						>
							<ChevronLeftIcon className="h-4 w-4" />
						</button>
						<span className="text-xs text-gray-600 px-2">
							{startIndex + 1}-{Math.min(endIndex, archivosPendientes.length)} de {archivosPendientes.length}
						</span>
						<button
							onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
							disabled={currentPage === totalPages}
							className="inline-flex items-center px-2 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
							title="Página siguiente"
						>
							<ChevronRightIcon className="h-4 w-4" />
						</button>
					</div>
				)}
			</div>

			<div className="overflow-x-auto">
				<table className="min-w-full divide-y divide-gray-200">
					<thead className="bg-gray-50">
						<tr>
							<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
								Archivo
							</th>
							<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
								Tamaño
							</th>
							<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
								Fecha
							</th>
							<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
								Método de Pago
							</th>
							<th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
								Acciones
							</th>
						</tr>
					</thead>
					<tbody className="bg-white divide-y divide-gray-200">
						{archivosPaginados.map((archivo) => (
							<tr key={archivo.id} className="hover:bg-gray-50">
								<td className="px-3 py-2 whitespace-nowrap">
									<div className="flex items-center space-x-2">
										<DocumentTextIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
										<span className="text-xs text-gray-900 truncate max-w-xs">
											{archivo.nombre_original || 'Sin nombre'}
										</span>
										{/* Estado de procesamiento en cola */}
										<ColaProcesamiento archivoId={archivo.id} onUpdate={() => loadPendingFiles()} />
									</div>
								</td>
								<td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
									{formatFileSize(archivo.archivo?.size)}
								</td>
								<td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
									{formatDate(archivo.fecha_subida)}
								</td>
								<td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
									{archivo.metodo_pago ? (
										<span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
											{archivo.metodo_pago === '17' ? 'Efectivo' :
											 archivo.metodo_pago === '18' ? 'Cheque' :
											 archivo.metodo_pago === '19' ? 'Tarjeta Crédito' :
											 archivo.metodo_pago === '20' ? 'Tarjeta Débito' :
											 archivo.metodo_pago === '21' ? 'Transferencia' :
											 archivo.metodo_pago === '22' ? 'Nota Crédito' :
											 archivo.metodo_pago === '23' ? 'Otras' : archivo.metodo_pago}
										</span>
									) : (
										<span className="text-gray-400">-</span>
									)}
								</td>
								<td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium">
									<div className="flex items-center justify-end space-x-1">
										{getImageUrl(archivo) && (
											<button
												onClick={() => viewImage(getImageUrl(archivo))}
												className="text-primary hover:text-primary/80 p-1"
												title="Ver imagen"
											>
												<EyeIcon className="h-4 w-4" />
											</button>
										)}
										<button
											onClick={() => handleDelete(archivo.id)}
											className="text-red-600 hover:text-red-900 p-1"
											title="Eliminar"
										>
											<TrashIcon className="h-4 w-4" />
										</button>
									</div>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{/* Modal de imagen */}
			{showImageModal && imageUrl && (
				<div 
					className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
					onClick={() => setShowImageModal(false)}
				>
					<div className="relative max-w-4xl max-h-full">
						<button
							onClick={() => setShowImageModal(false)}
							className="absolute top-2 right-2 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-75 z-10"
						>
							<XMarkIcon className="h-6 w-6" />
						</button>
						<img
							src={imageUrl}
							alt="Factura"
							className="max-w-full max-h-[90vh] object-contain rounded-lg"
							onClick={(e) => e.stopPropagation()}
						/>
					</div>
				</div>
			)}
			</div>
		</div>
	);
}

